// Latency telemetry: keystroke→painted-echo samples, RTT samples and stalls.
// Rolling percentiles are kept in memory; new samples are POSTed to /api/telemetry every 10 s.

import type { TransportKind } from './transport';

export const STALL_MS = 300;
const WINDOW = 2000;

/** End-to-end action timings (recordOp): the person's action → the visible result. */
export type OpName = 'session.create' | 'session.attach' | 'session.prompt';
const OP_NAMES: OpName[] = ['session.create', 'session.attach', 'session.prompt'];
/** A sample that is negative or beyond this is not a timing (dropped). */
const OP_MAX_MS = 10 * 60 * 1000;
/** The most ops one flush sends. */
const OP_BATCH = 200;
/** The ops ring's size: recent samples a debug/UI reader can percentile over. */
const OP_WINDOW = 500;

export interface Percentiles {
  n: number;
  p50: number | null;
  p95: number | null;
  p99: number | null;
  max: number | null;
  mean: number | null;
}

export interface Stats {
  echo: Percentiles;
  rtt: Percentiles;
  stalls: number;
  /** Printable keystrokes whose echo was never seen within 2 s (non-echoing input). */
  unmatched: number;
  transport: TransportKind | null;
  /** End-to-end action timings, by action (recordOp). */
  ops: Record<OpName, Percentiles>;
  /** Action samples recorded as failed (ok:false), by action. */
  opFailures: Record<OpName, number>;
}

class Ring {
  private buf: number[] = [];
  private cap: number;
  constructor(cap: number) {
    this.cap = cap;
  }
  push(v: number) {
    this.buf.push(v);
    if (this.buf.length > this.cap) this.buf.splice(0, this.buf.length - this.cap);
  }
  clear() {
    this.buf = [];
  }
  values() {
    return this.buf;
  }
}

function pct(values: number[]): Percentiles {
  const n = values.length;
  if (!n) return { n, p50: null, p95: null, p99: null, max: null, mean: null };
  const s = [...values].sort((a, b) => a - b);
  // Nearest-rank percentile.
  const at = (p: number) => s[Math.min(n - 1, Math.max(0, Math.ceil((p / 100) * n) - 1))];
  const r = (v: number) => Math.round(v * 10) / 10;
  return {
    n,
    p50: r(at(50)),
    p95: r(at(95)),
    p99: r(at(99)),
    max: r(s[n - 1]),
    mean: r(s.reduce((a, b) => a + b, 0) / n),
  };
}

/** One keystroke's echo, stage by stage (performance.now() ms): see bench/term. */
export interface EchoTrace {
  key: number;
  sent: number;
  arrived: number;
  written: number;
  frame: number;
  /** The frame's start time as requestAnimationFrame reports it. */
  vsync: number;
}

class Telemetry {
  private echo = new Ring(WINDOW);
  private traces: EchoTrace[] = [];
  private rtt = new Ring(500);
  private stallCount = 0;
  private unmatched = 0;
  private outEcho: number[] = [];
  private outRtt: number[] = [];
  private outStalls: number[] = [];
  /** Keys whose echo never came within the 2 s window since the last flush (sent as `lost`). */
  private outLost = 0;
  private ops = new Map<OpName, Ring>();
  private opFailures = new Map<OpName, number>();
  private outOps: { name: OpName; ms: number; ok: boolean }[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private listeners = new Set<() => void>();
  private notifyScheduled = false;
  transport: TransportKind | null = null;

  recordEchoTrace(t: EchoTrace) {
    if (this.traces.length >= 5000) this.traces.shift();
    this.traces.push(t);
  }

  echoTraces(): EchoTrace[] {
    return this.traces.slice();
  }

  recordEcho(ms: number) {
    this.echo.push(ms);
    this.outEcho.push(Math.round(ms * 100) / 100);
    if (ms > STALL_MS) {
      this.stallCount++;
      this.outStalls.push(Math.round(ms));
    }
    this.changed();
  }

  recordRtt(ms: number) {
    this.rtt.push(ms);
    this.outRtt.push(Math.round(ms * 100) / 100);
    this.changed();
  }

  /** A keystroke whose echo never arrived within the window (non-echoing input, a swallowed
   *  key, a stalled stream): counted, and sent to the hub as `lost`, so latency can't look
   *  better by leaving out the keys that never came back. */
  recordUnmatched() {
    this.unmatched++;
    this.outLost++;
  }

  /**
   * One action's time, from the person's action to the visible result. `ok:false` means it failed:
   * the sample never counts as a slow success. Best-effort (invalid samples are dropped).
   */
  recordOp(name: OpName, ms: number, ok = true) {
    if (!Number.isFinite(ms) || ms < 0 || ms > OP_MAX_MS) return;
    // A failure is counted, never read as a slow success — the same rule the hub keeps
    // (internal/hub/telemetry.go), so these percentiles and the summary agree.
    if (ok) this.opRing(name).push(ms);
    else this.opFailures.set(name, (this.opFailures.get(name) ?? 0) + 1);
    if (this.outOps.length < OP_BATCH) this.outOps.push({ name, ms: Math.round(ms * 100) / 100, ok });
    this.changed();
  }

  private opRing(name: OpName): Ring {
    let r = this.ops.get(name);
    if (!r) {
      r = new Ring(OP_WINDOW);
      this.ops.set(name, r);
    }
    return r;
  }

  reset() {
    this.echo.clear();
    this.traces = [];
    this.rtt.clear();
    this.stallCount = 0;
    this.unmatched = 0;
    this.ops.clear();
    this.opFailures.clear();
    this.changed();
  }

  stats(): Stats {
    const ops = {} as Record<OpName, Percentiles>;
    const opFailures = {} as Record<OpName, number>;
    for (const n of OP_NAMES) {
      ops[n] = pct(this.ops.get(n)?.values() ?? []);
      opFailures[n] = this.opFailures.get(n) ?? 0;
    }
    return {
      echo: pct(this.echo.values()),
      rtt: pct(this.rtt.values()),
      stalls: this.stallCount,
      unmatched: this.unmatched,
      transport: this.transport,
      ops,
      opFailures,
    };
  }

  /** Coalesced change notification for UI (at most once per frame-ish). */
  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private changed() {
    if (this.notifyScheduled) return;
    this.notifyScheduled = true;
    setTimeout(() => {
      this.notifyScheduled = false;
      this.listeners.forEach((l) => l());
    }, 250);
  }

  start() {
    if (this.timer) return;
    this.timer = setInterval(() => void this.flush(), 10_000);
    addEventListener('pagehide', () => void this.flush(true));
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  async flush(beacon = false) {
    if (!this.outEcho.length && !this.outRtt.length && !this.outStalls.length && !this.outOps.length && !this.outLost) return;
    const body = JSON.stringify({
      echo: this.outEcho,
      rtt: this.outRtt,
      stalls: this.outStalls,
      transport: this.transport ?? 'ws',
      // Only when there are some: with none, the body is the shape it always was.
      ...(this.outOps.length ? { ops: this.outOps } : {}),
      ...(this.outLost ? { lost: this.outLost } : {}),
    });
    this.outEcho = [];
    this.outRtt = [];
    this.outStalls = [];
    this.outOps = [];
    this.outLost = 0;
    try {
      if (beacon && navigator.sendBeacon) {
        navigator.sendBeacon('/api/telemetry', new Blob([body], { type: 'application/json' }));
        return;
      }
      await fetch('/api/telemetry', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body,
        credentials: 'same-origin',
        keepalive: true,
      });
    } catch {
      // Telemetry is best-effort.
    }
  }
}

export const telemetry = new Telemetry();
